import { message } from 'antd';
import { CancelToken } from 'axios';
import { NotificationPayload, Recipient } from 'models/notification';

import { Api } from './api';
import { BaseGetAllParams, DEFAULT_PAGE_SIZE, All, AllInfinite } from './defaults';

export function GetFactory<T>(baseUrl: string) {
  return async function Get(id: string, options?: { cancelToken?: CancelToken }) {
    const result = await Api.get<any, T>(`/${baseUrl}/${id}`, undefined, options?.cancelToken);
    return result;
  };
}

export function GetAllInfiniteFactory<T, E extends BaseGetAllParams>(baseUrl: string) {
  return async function GetAll(params: E, options?: { cancelToken?: CancelToken }) {
    if (params.pageSize && params.pageSize > DEFAULT_PAGE_SIZE) {
      params.pageSize = DEFAULT_PAGE_SIZE;
    }
    const result = await Api.get<any, AllInfinite<T>>(
      `/${baseUrl}`,
      {
        ...params,
      },
      options?.cancelToken,
    );
    return result;
  };
}

export function GetAllFactory<T, E extends BaseGetAllParams>(baseUrl: string) {
  return async function GetAll(params: E, options?: { cancelToken?: CancelToken }) {
    if (params.pageSize && params.pageSize > DEFAULT_PAGE_SIZE) {
      params.pageSize = DEFAULT_PAGE_SIZE;
    }
    const result = await Api.get<any, All<T>>(
      `/${baseUrl}`,
      {
        ...params,
      },
      options?.cancelToken,
    );
    return result;
  };
}

export function CreateFactory<T>(baseUrl: string, serviceName: string) {
  return async function Create(data: Partial<T>, options: { notify: boolean; cancelToken?: CancelToken } = { notify: true }) {
    const result = await Api.post<Partial<T>, T>(`/${baseUrl}`, data, options.cancelToken);
    if (options.notify) {
      message.success(`${serviceName} created successfully`);
    }
    return result;
  };
}

export function PostFactory<T>(baseUrl: string, serviceName: string) {
  return async function Post(data: Partial<T>, options: { cancelToken?: CancelToken } = {}) {
    const result = await Api.post<Partial<T>, T>(`/${baseUrl}`, data, options.cancelToken);
    return result;
  };
}

export function DeleteFactory(baseUrl: string, serviceName: string) {
  return async function Delete(id: string, options: { notify: boolean; cancelToken?: CancelToken } = { notify: true }) {
    const result = await Api.delete(`/${baseUrl}/${id}`, options.cancelToken);
    if (options.notify) {
      message.success(`${serviceName} deleted successfully`);
    }
    return result;
  };
}

export function UpdateFactory<T>(baseUrl: string, serviceName: string) {
  return async function Update(id: string, data: Partial<T>, options: { notify: boolean; cancelToken?: CancelToken } = { notify: true }) {
    const result = await Api.put<Partial<T>, T>(`/${baseUrl}/${id}`, data, options?.cancelToken);
    if (options.notify) {
      message.success(`${serviceName} updated successfully`);
    }
    return result;
  };
}

export function NotifyFactory(baseUrl: string) {
  return async function Notify(id: string, data: NotificationPayload, options: { notify: boolean; cancelToken?: CancelToken } = { notify: true }) {
    const result = await Api.post<NotificationPayload, void>(`/${baseUrl}/${id}/notify`, data, options?.cancelToken);
    if (options.notify) {
      message.success(`Notification sent successfully`);
    }
    return result;
  };
}

export function GetRecipientsFactory(baseUrl: string) {
  return async function GetRecipients(id: string, options?: { cancelToken?: CancelToken }) {
    const result = await Api.get<any, Recipient[]>(`/${baseUrl}/${id}/recipients`, options?.cancelToken);
    return result;
  };
}
